import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { placesPageQueryOptions } from "common/hooks/places/placesQueryOptions";
import type { PlacesCityParams } from "common/types/places";
import { PlacesListing } from "@/components/Places/PlacesListing";
import { PlacesUnavailable } from "@/components/Places/PlacesUnavailable";
import { buildPlacesPageHead, parsePlacesSearch } from "@/lib/placesPage";
import { loadPlacesPage } from "@/lib/placesPageLoader";

const isProduction =
  import.meta.env.VITE_APPLICATION_ENVIRONMENT === "Production";

// /places/<city>/<area>/<type>
export const Route = createFileRoute("/places/$citySlug_/$segment_/$typeSlug")({
  ssr: true,
  component: PlacesAreaType,
  errorComponent: PlacesUnavailable,
  validateSearch: (search: Record<string, unknown>): PlacesCityParams => parsePlacesSearch(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, params, deps: { search } }) =>
    loadPlacesPage(context.queryClient, params.citySlug, [params.segment, params.typeSlug], search),
  head: ({ loaderData, params }) =>
    buildPlacesPageHead(
      loaderData?.page,
      { citySlug: params.citySlug, areaSlug: params.segment, typeSlug: params.typeSlug },
      loaderData?.search ?? {},
      loaderData?.canonicalOrigin ?? "",
      isProduction,
    ),
});

function PlacesAreaType() {
  const { citySlug, segment, typeSlug } = Route.useParams();
  const search = Route.useSearch();
  const { data } = useQuery(placesPageQueryOptions(citySlug, [segment, typeSlug], search));

  if (!data?.data) {
    return null;
  }

  return (
    <PlacesListing data={data.data} citySlug={citySlug} areaSlug={segment} typeSlug={typeSlug} search={search} />
  );
}
